import { z } from 'zod';

import type {
  GetStationOperationsStatusRequest,
  GetStationOperationsStatusResponse,
  JoinStoreOperatingSessionRequest,
  JoinStoreOperatingSessionResponse,
  JoinStoreOperatingSessionCloudRequest,
  JoinStoreOperatingSessionCloudResponse,
  GetStoreReadinessRequest,
  GetStoreReadinessResponse,
  ReportStoreReadinessRequest,
  ReportStoreReadinessResponse,
  ReportStoreReadinessCloudRequest,
  ReportStoreReadinessCloudResponse,
  StationOperationsStatusDto,
  StoreOperatingSessionDto,
  StoreReadinessDto,
  StoreReadinessParticipantDto,
  UpsertStoreOperatingSessionRequest,
  UpsertStoreOperatingSessionResponse,
} from './station-operations.dto.js';

const isoDatetimeSchema = z.iso.datetime();
const isoDateSchema = z.iso.date();
const timeZoneSchema = z.string().trim().min(1);

export const StoreOperatingSessionSchema: z.ZodType<StoreOperatingSessionDto> = z.object({
  sessionKey: z.string().trim().min(1),
  companyId: z.uuid(),
  storeId: z.uuid(),
  businessDate: isoDateSchema,
  timeZone: timeZoneSchema,
  openedAt: isoDatetimeSchema,
  closedAt: isoDatetimeSchema.nullable(),
  primaryStationId: z.uuid(),
  cloudVisibleAt: isoDatetimeSchema.nullable(),
});

export const StoreReadinessParticipantSchema: z.ZodType<StoreReadinessParticipantDto> = z.object({
  stationId: z.uuid(),
  joinedAt: isoDatetimeSchema,
  persistedLocally: z.boolean(),
  lastReadinessConfirmedAt: isoDatetimeSchema.nullable(),
  pendingBlockingEventCount: z.number().int().min(0),
  blockingReason: z.enum([
    'none',
    'cloud_visibility_delay',
    'cloud_unavailable',
    'cloud_auth_required',
    'join_required',
    'joined_station_pending_sync',
    'positive_confirmation_missing',
  ] as const),
  lastHeartbeatAt: isoDatetimeSchema.nullable(),
});

export const StoreReadinessSchema: z.ZodType<StoreReadinessDto> = z.object({
  status: z.enum(['not_required', 'ready', 'blocked', 'unknown'] as const),
  blockingReason: z.enum([
    'none',
    'cloud_visibility_delay',
    'cloud_unavailable',
    'cloud_auth_required',
    'join_required',
    'joined_station_pending_sync',
    'positive_confirmation_missing',
  ] as const),
  checkedAt: isoDatetimeSchema,
  participants: z.array(StoreReadinessParticipantSchema),
});

export const StationOperationsStatusSchema: z.ZodType<StationOperationsStatusDto> = z.object({
  companyId: z.uuid(),
  storeId: z.uuid(),
  stationId: z.uuid(),
  operatingModel: z.enum(['mono_station', 'multi_station'] as const),
  stationRole: z.enum(['mono', 'primary', 'secondary'] as const),
  businessDate: isoDateSchema,
  timeZone: timeZoneSchema,
  localOperationState: z.enum([
    'not_started',
    'mono_operational_local',
    'primary_operational_local',
    'secondary_join_required',
    'secondary_joined_local',
  ] as const),
  cloudVisibilityState: z.enum([
    'not_required',
    'cloud_session_not_yet_visible',
    'cloud_session_visible',
  ] as const),
  session: StoreOperatingSessionSchema.nullable(),
  readiness: StoreReadinessSchema,
  joinRequired: z.boolean(),
  operationBlocked: z.boolean(),
  blockReasonCode: z.string().nullable(),
  blockReasonMessage: z.string().nullable(),
});

export const GetStationOperationsStatusRequestSchema: z.ZodType<GetStationOperationsStatusRequest> = z.object({
  companyId: z.uuid(),
});

export const GetStationOperationsStatusResponseSchema: z.ZodType<GetStationOperationsStatusResponse> = z.object({
  status: StationOperationsStatusSchema,
});

export const JoinStoreOperatingSessionRequestSchema: z.ZodType<JoinStoreOperatingSessionRequest> = z.object({
  companyId: z.uuid(),
});

export const JoinStoreOperatingSessionResponseSchema: z.ZodType<JoinStoreOperatingSessionResponse> = z.object({
  status: StationOperationsStatusSchema,
});

export const ReportStoreReadinessRequestSchema: z.ZodType<ReportStoreReadinessRequest> = z.object({
  companyId: z.uuid(),
});

export const ReportStoreReadinessResponseSchema: z.ZodType<ReportStoreReadinessResponse> = z.object({
  status: StationOperationsStatusSchema,
});

export const UpsertStoreOperatingSessionRequestSchema: z.ZodType<UpsertStoreOperatingSessionRequest> = z.object({
  companyId: z.uuid(),
  storeId: z.uuid(),
  stationId: z.uuid(),
  businessDate: isoDateSchema,
  sessionKey: z.string().trim().min(1),
  timeZone: timeZoneSchema,
  openedAt: isoDatetimeSchema,
});

export const UpsertStoreOperatingSessionResponseSchema: z.ZodType<UpsertStoreOperatingSessionResponse> = z.object({
  session: StoreOperatingSessionSchema,
});

export const JoinStoreOperatingSessionCloudRequestSchema: z.ZodType<JoinStoreOperatingSessionCloudRequest> = z.object({
  companyId: z.uuid(),
  storeId: z.uuid(),
  stationId: z.uuid(),
  businessDate: isoDateSchema,
  sessionKey: z.string().trim().min(1),
  joinedAt: isoDatetimeSchema,
  persistedLocally: z.boolean(),
});

export const JoinStoreOperatingSessionCloudResponseSchema: z.ZodType<JoinStoreOperatingSessionCloudResponse> = z.object({
  participant: StoreReadinessParticipantSchema,
});

export const ReportStoreReadinessCloudRequestSchema: z.ZodType<ReportStoreReadinessCloudRequest> = z.object({
  companyId: z.uuid(),
  storeId: z.uuid(),
  stationId: z.uuid(),
  businessDate: isoDateSchema,
  sessionKey: z.string().trim().min(1),
  pendingBlockingEventCount: z.number().int().min(0),
  blockingReason: z.enum([
    'none',
    'cloud_visibility_delay',
    'cloud_unavailable',
    'cloud_auth_required',
    'join_required',
    'joined_station_pending_sync',
    'positive_confirmation_missing',
  ] as const),
  persistedLocally: z.boolean(),
  lastReadinessConfirmedAt: isoDatetimeSchema.nullable(),
  lastHeartbeatAt: isoDatetimeSchema.nullable().optional(),
});

export const ReportStoreReadinessCloudResponseSchema: z.ZodType<ReportStoreReadinessCloudResponse> = z.object({
  readiness: StoreReadinessSchema,
});

export const GetStoreReadinessRequestSchema: z.ZodType<GetStoreReadinessRequest> = z.object({
  companyId: z.uuid(),
  storeId: z.uuid(),
  businessDate: isoDateSchema,
});

export const GetStoreReadinessResponseSchema: z.ZodType<GetStoreReadinessResponse> = z.object({
  readiness: StoreReadinessSchema,
});
